/**
 * The system-event catalog as the UI shows it (TRUS-684 / TRUS-685): our own
 * labels for every known type and family, and the grouping used by both the
 * channel "Connect events" dialog and Settings → Notifications → "Events I
 * follow". The server's label is the fallback for a type we do not know.
 */

import {
  SYSTEM_EVENT_GROUPS,
  defaultFilterKinds,
  familyOf,
  type SystemEventFamily,
  type SystemEventFilterKind,
  type SystemEventType,
} from '@/services/systemEventsApi';

/** react-query key for the types catalog (shared by every surface). */
export const systemEventTypesKey = ['system-event-types'] as const;

export type TFn = (key: string, fallback?: string, opts?: Record<string, unknown>) => string;

export const REPO_LABEL_KEYS: Record<string, [string, string]> = {
  backend: ['messaging.systemEvents.repos.backend', 'Backend (BE)'],
  frontend: ['messaging.systemEvents.repos.frontend', 'Frontend (FE)'],
  mobile: ['messaging.systemEvents.repos.mobile', 'Mobile'],
};

export const GROUP_LABEL_KEYS: Record<SystemEventFamily, [string, string]> = {
  agent: ['messaging.systemEvents.groups.agent', 'Agents'],
  git: ['messaging.systemEvents.groups.git', 'Code'],
  deploy: ['messaging.systemEvents.groups.deploy', 'Deploys'],
  signatures: ['messaging.systemEvents.groups.signatures', 'Signatures'],
  tasks: ['messaging.systemEvents.groups.tasks', 'Tasks'],
  workflows: ['messaging.systemEvents.groups.workflows', 'Workflows'],
  member_tasks: ['messaging.systemEvents.groups.memberTasks', 'Member tasks'],
  automation: ['messaging.systemEvents.groups.automation', 'Automations'],
  other: ['messaging.systemEvents.groups.other', 'Other'],
};

/** Our own label for each known type (the server's label is the fallback). */
export const TYPE_LABEL_KEYS: Record<string, [string, string]> = {
  'rca.held_for_review': ['messaging.systemEvents.types.rcaHeld', 'RCA needs review'],
  'rca.failed': ['messaging.systemEvents.types.rcaFailed', 'RCA failed'],
  'rca.executed': ['messaging.systemEvents.types.rcaExecuted', 'Change applied'],
  'rca.creator_replied': ['messaging.systemEvents.types.rcaCreatorReplied', 'Ticket creator replied'],
  'pr.ready_to_merge': ['messaging.systemEvents.types.prReady', 'PR ready to merge'],
  'pr.merged': ['messaging.systemEvents.types.prMerged', 'PR merged'],
  'deploy.succeeded': ['messaging.systemEvents.types.deploySucceeded', 'Deploy succeeded'],
  'deploy.failed': ['messaging.systemEvents.types.deployFailed', 'Deploy failed'],
  'deploy.released': ['messaging.systemEvents.types.deployReleased', 'App releases'],
  'signature.requested': ['messaging.systemEvents.types.signatureRequested', 'Signature requested'],
  'signature.completed': ['messaging.systemEvents.types.signatureCompleted', 'Signature completed'],
  'signature.declined': ['messaging.systemEvents.types.signatureDeclined', 'Signature declined or expired'],
  'task.assigned': ['messaging.systemEvents.types.taskAssigned', 'Task assigned'],
  'task.due_soon': ['messaging.systemEvents.types.taskDueSoon', 'Task due within 24 hours'],
  'task.overdue': ['messaging.systemEvents.types.taskOverdue', 'Task overdue'],
  'task.completed': ['messaging.systemEvents.types.taskCompleted', 'Task completed'],
  'workflow.step_assigned': ['messaging.systemEvents.types.workflowStepAssigned', 'Workflow step assigned'],
  'workflow.step_overdue': ['messaging.systemEvents.types.workflowStepOverdue', 'Workflow step overdue'],
  'workflow.completed': ['messaging.systemEvents.types.workflowCompleted', 'Workflow completed'],
  'workflow.failed': ['messaging.systemEvents.types.workflowFailed', 'Workflow failed'],
  'workflow.notification': ['messaging.systemEvents.types.workflowNotification', 'Workflow message'],
  'member_task.assigned': ['messaging.systemEvents.types.memberTaskAssigned', 'Member task assigned'],
  'member_task.overdue': ['messaging.systemEvents.types.memberTaskOverdue', 'Member task overdue'],
  'member_task.completed': ['messaging.systemEvents.types.memberTaskCompleted', 'Member task completed'],
};

export function typeLabel(type: string, types: SystemEventType[], t: TFn): string {
  const known = TYPE_LABEL_KEYS[type];
  const serverLabel = types.find((x) => x.type === type)?.label;
  return known ? t(known[0], known[1]) : serverLabel || type;
}

export function groupLabel(family: SystemEventFamily, t: TFn): string {
  const [key, fallback] = GROUP_LABEL_KEYS[family] ?? GROUP_LABEL_KEYS.other;
  return t(key, fallback);
}

export function repoLabel(repo: string, t: TFn): string {
  const known = REPO_LABEL_KEYS[repo];
  return known ? t(known[0], known[1]) : repo;
}

/** A type's family, tolerating objects that did not go through parseTypes. */
export function familyOfType(type: SystemEventType): SystemEventFamily {
  return type.family || familyOf(type.type);
}

/** A type's filters, tolerating objects that did not go through parseTypes. */
export function filtersOfType(type: SystemEventType): SystemEventFilterKind[] {
  return Array.isArray(type.filters) ? type.filters : defaultFilterKinds(familyOfType(type));
}

export interface EventTypeGroup {
  id: SystemEventFamily;
  types: SystemEventType[];
}

/**
 * Group types by family in display order. Within a family, known types come
 * first in our order, then any the server added. Unknown families → "Other".
 */
export function groupTypes(types: SystemEventType[]): EventTypeGroup[] {
  const groups: EventTypeGroup[] = [];
  const seen = new Set<string>();
  for (const g of SYSTEM_EVENT_GROUPS) {
    const known = g.types
      .map((type) => types.find((x) => x.type === type && familyOfType(x) === g.id))
      .filter((x): x is SystemEventType => !!x);
    const extra = types.filter((x) => familyOfType(x) === g.id && !g.types.includes(x.type));
    const members = [...known, ...extra];
    members.forEach((m) => seen.add(m.type));
    if (members.length) groups.push({ id: g.id, types: members });
  }
  const rest = types.filter((x) => !seen.has(x.type));
  if (rest.length) groups.push({ id: 'other', types: rest });
  return groups;
}

/** The filters that apply to any of these types. */
export function filterKindsFor(types: SystemEventType[]): Set<SystemEventFilterKind> {
  const out = new Set<SystemEventFilterKind>();
  for (const type of types) filtersOfType(type).forEach((k) => out.add(k));
  return out;
}

export function parseServices(text: string): string[] {
  const out: string[] = [];
  for (const raw of text.split(/[,\n]/)) {
    const s = raw.trim();
    if (s && !out.includes(s)) out.push(s);
  }
  return out;
}

export const toggleValue = <T,>(list: T[], value: T, on: boolean): T[] =>
  on ? (list.includes(value) ? list : [...list, value]) : list.filter((v) => v !== value);
